// Date formatting utilities

/**
 * Convert Firestore Timestamp or various date formats to Date object
 */
const toDate = (date) => {
  if (!date) return null;

  // Already a Date object
  if (date instanceof Date) return date;

  // Firestore Timestamp object (has _seconds or seconds property)
  if (date._seconds !== undefined) {
    return new Date(date._seconds * 1000);
  }
  if (date.seconds !== undefined) {
    return new Date(date.seconds * 1000);
  }

  // Firestore Timestamp with toDate method
  if (typeof date.toDate === 'function') {
    return date.toDate();
  }

  // String format
  if (typeof date === 'string') {
    // Check for German date format: DD.MM.YY or DD.MM.YYYY
    const germanMatch = date.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
    if (germanMatch) {
      const day = parseInt(germanMatch[1], 10);
      const month = parseInt(germanMatch[2], 10) - 1; // JS months are 0-indexed
      let year = parseInt(germanMatch[3], 10);
      // Handle 2-digit year
      if (year < 100) {
        year += year > 50 ? 1900 : 2000;
      }
      return new Date(year, month, day, 12, 0, 0); // Noon to avoid timezone issues
    }
    // ISO string or other format
    return new Date(date);
  }

  // Number (timestamp in milliseconds)
  if (typeof date === 'number') {
    return new Date(date);
  }

  return null;
};

/**
 * Format date to German locale (DD.MM.YYYY)
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '-';

  const d = toDate(date);
  if (!d || isNaN(d.getTime())) return '-';

  const defaultOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options,
  };

  return d.toLocaleDateString('de-DE', defaultOptions);
};

/**
 * Format date to short German locale (DD.MM.YY)
 */
export const formatDateShort = (date) => {
  return formatDate(date, { year: '2-digit' });
};

/**
 * Format time to German locale (HH:mm)
 */
export const formatTime = (date) => {
  if (!date) return '-';

  const d = toDate(date);
  if (!d || isNaN(d.getTime())) return '-';

  return d.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format datetime to German locale (DD.MM.YYYY HH:mm)
 */
export const formatDateTime = (date) => {
  if (!date) return '-';

  const d = toDate(date);
  if (!d || isNaN(d.getTime())) return '-';

  return `${formatDate(d)} ${formatTime(d)}`;
};

/**
 * Format relative time (e.g., "vor 2 Stunden")
 */
export const formatRelativeTime = (date) => {
  if (!date) return '-';

  const d = toDate(date);
  if (!d || isNaN(d.getTime())) return '-';

  const now = new Date();
  const diffMs = now - d;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Minute${diffMin !== 1 ? 'n' : ''}`;
  if (diffHour < 24) return `vor ${diffHour} Stunde${diffHour !== 1 ? 'n' : ''}`;
  if (diffDay < 7) return `vor ${diffDay} Tag${diffDay !== 1 ? 'en' : ''}`;

  return formatDate(date);
};

/**
 * Format hours (e.g., "8,5 Std.")
 */
export const formatHours = (hours) => {
  if (hours === null || hours === undefined) return '-';

  const formatted = parseFloat(hours).toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  return `${formatted} Std.`;
};

/**
 * Format number to German locale
 */
export const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined) return '-';

  return parseFloat(num).toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format currency (EUR)
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '-';

  return parseFloat(amount).toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });
};

/**
 * Get month name in German
 */
export const getMonthName = (month, short = false) => {
  const date = new Date(2000, month - 1, 1);
  return date.toLocaleDateString('de-DE', { month: short ? 'short' : 'long' });
};

/**
 * Get day name in German
 */
export const getDayName = (date, short = false) => {
  const d = new Date(date);
  return d.toLocaleDateString('de-DE', { weekday: short ? 'short' : 'long' });
};

/**
 * Parse German date format (DD.MM.YYYY) to Date object
 */
export const parseGermanDate = (dateStr) => {
  if (!dateStr) return null;

  const parts = dateStr.split('.');
  if (parts.length !== 3) return null;

  let [day, month, year] = parts.map(Number);

  // Handle 2-digit year
  if (year < 100) {
    year += year > 50 ? 1900 : 2000;
  }

  return new Date(year, month - 1, day);
};

/**
 * Get status badge color class
 */
export const getStatusColor = (status) => {
  const colors = {
    // Vacation/Order status
    pending: 'badge-warning',
    approved: 'badge-success',
    rejected: 'badge-danger',
    draft: 'badge-gray',
    submitted: 'badge-info',
    in_progress: 'badge-warning',
    ordered: 'badge-success',
    completed: 'badge-success',
    partially_completed: 'badge-warning',
    archived: 'badge-gray',

    // Sick report status
    active: 'badge-danger',
    ended: 'badge-gray',
    ended_by_checkin: 'badge-gray',
    cancelled_sick: 'badge-warning',
    cancelled_same_day: 'badge-info',

    // Project status
    paused: 'badge-warning',

    // User roles
    admin: 'badge-info',
    bauleiter: 'badge-warning',
    mitarbeiter: 'badge-gray',
  };

  return colors[status] || 'badge-gray';
};

/**
 * Get status label in German
 */
export const getStatusLabel = (status) => {
  const labels = {
    // Vacation/Order status
    pending: 'Ausstehend',
    approved: 'Genehmigt',
    rejected: 'Abgelehnt',
    draft: 'Entwurf',
    submitted: 'Eingereicht',
    in_progress: 'In Bearbeitung',
    ordered: 'Bestellt',
    completed: 'Abgeschlossen',
    partially_completed: 'Teilweise bestellt',
    archived: 'Archiviert',

    // Sick report status
    active: 'Aktiv',
    ended: 'Beendet',
    ended_by_checkin: 'Beendet (Einstempeln)',
    cancelled_sick: 'Storniert (Krankheit)',
    cancelled_same_day: 'Storniert (gleicher Tag)',

    // Project status
    paused: 'Pausiert',

    // User roles
    admin: 'Administrator',
    bauleiter: 'Bauleiter',
    mitarbeiter: 'Mitarbeiter',
  };

  return labels[status] || status;
};
